/**
 * Pure data shaping for Lead Overview “AI copilot” UI — no React.
 */

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, '_').trim();

/** Overview API exposes recovery on `lead_recovery`; older payloads used `not_interested_recovery`. */
export function getOverviewRecoveryPayload(overview) {
  if (!overview || typeof overview !== 'object') return null;
  return overview.lead_recovery ?? overview.not_interested_recovery ?? null;
}

export function getOverviewRecoveryScore(overview) {
  const p = getOverviewRecoveryPayload(overview);
  const n = Number(p?.recovery_score);
  return !Number.isNaN(n) ? n : 0;
}

/**
 * Maps `lead_recovery` / legacy `not_interested_recovery` to the shape LeadRecovery expects
 * (`recoverable` from `recovery_eligible` or score heuristics when needed).
 */
export function normalizeOverviewLeadRecovery(overview, leadDetails) {
  const raw = getOverviewRecoveryPayload(overview);
  if (!raw || typeof raw !== 'object') return null;
  const notRecoverable = Boolean(leadDetails?.is_not_recoverable);
  let recoverable;
  if (typeof raw.recoverable === 'boolean') {
    recoverable = raw.recoverable && !notRecoverable;
  } else if (raw.recovery_eligible === true) {
    recoverable = !notRecoverable;
  } else if (raw.recovery_eligible === false) {
    recoverable = false;
  } else {
    const s = Number(raw.recovery_score);
    recoverable = !notRecoverable && !Number.isNaN(s) && s >= 40;
  }
  return { ...raw, recoverable };
}

/** Progress rows from `recovery_llm_score` / `recovery_rule_score` when present. */
export function getRecoveryReadinessBarsFromOverview(overview) {
  const lr = getOverviewRecoveryPayload(overview);
  if (!lr) return null;
  const ai = lr.recovery_llm_score;
  const rule = lr.recovery_rule_score;
  if (ai == null && rule == null) return null;
  const rows = [];
  if (ai != null) {
    const n = Number(ai);
    if (!Number.isNaN(n)) rows.push({ label: 'AI score', score: n });
  }
  if (rule != null) {
    const n = Number(rule);
    if (!Number.isNaN(n)) rows.push({ label: 'Rule score', score: n });
  }
  return rows.length ? rows : null;
}

export function getLeadDialNumber(lead) {
  if (!lead) return '';
  const ci = lead.contact_info || {};
  return (
    ci.mobile ||
    ci.phone ||
    lead.mobile ||
    lead.phone ||
    lead.customer_mobile ||
    ''
  );
}

/** Date + time line from overview `upcoming_task` (matches LeadOverview snapshot shape). */
function formatUpcomingTaskWhen(upcomingTask) {
  if (!upcomingTask) return '';
  const d = String(upcomingTask.date || '').trim();
  const t = String(upcomingTask.time || '').trim();
  if (d && t) return `${d} · ${t}`;
  return d || t || '';
}

/** ~12–14 words max for action bar headline */
export function truncateWords(text, maxWords = 14) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  const words = t.split(' ');
  if (words.length <= maxWords) return t;
  return `${words.slice(0, maxWords).join(' ')}…`;
}

/**
 * @returns {{ kind: 'healthy' | 'recoverable' | 'review', confidence: 'High'|'Medium'|'Low' }}
 */
export function deriveVerdictKind({
  lead,
  overview,
  stats,
  conversionProb,
}) {
  const st = norm(lead?.status);
  const stg = norm(lead?.stage);
  const isNI = st === 'not_interested';
  const isJunk = stg === 'junk' || stg === 'archived';
  const recoveryScore = getOverviewRecoveryScore(overview);
  const dormant = Boolean(overview?.lead_aging?.is_dormant);
  const urgency = Boolean(overview?.urgency?.show);
  const eng = stats?.engagement_score ?? 0;
  const band = conversionProb?.band;
  const score = Number(conversionProb?.score);
  const hasMixed =
    (band === 'high' && (isNI || isJunk)) ||
    (band === 'low' && !isNI && !isJunk && eng > 40);

  if (isNI && recoveryScore < 35 && band === 'low') {
    return { kind: 'healthy', confidence: 'High' };
  }
  if (isJunk && eng < 15 && !dormant && band !== 'high') {
    return { kind: 'healthy', confidence: 'Medium' };
  }
  if (hasMixed) {
    return { kind: 'review', confidence: 'Medium' };
  }
  if (isNI && recoveryScore >= 35) {
    return { kind: 'recoverable', confidence: recoveryScore >= 60 ? 'High' : 'Medium' };
  }
  if (isJunk && (eng >= 20 || band === 'high')) {
    return { kind: 'recoverable', confidence: 'Medium' };
  }
  if (dormant || urgency) {
    return { kind: 'recoverable', confidence: urgency ? 'High' : 'Medium' };
  }
  if (band === 'high') {
    return { kind: 'healthy', confidence: score >= 70 ? 'High' : 'Medium' };
  }
  if (band === 'low' && (overview?.lead_aging?.days_since_last_contact ?? 0) > 30) {
    return { kind: 'recoverable', confidence: 'Medium' };
  }
  return { kind: 'review', confidence: 'Low' };
}

export function buildAiHeadline({
  conversationInsights,
  conversionProb,
  overview,
  stats,
  verdict,
  lead,
}) {
  const primary = conversationInsights?.primary_next_action;
  if (primary?.source_alert_type === 'upcoming_calendar_task') {
    // Prefer the backend-provided detail, since it already includes the scheduled
    // IST date/time (and should match `conversation_insights.primary_next_action`).
    if (primary.detail) return truncateWords(primary.detail);

    const when = formatUpcomingTaskWhen(overview?.upcoming_task);
    const head = String(primary.title || '').trim();
    if (when && head) return truncateWords(`${head} — ${when}`);
    if (head) return truncateWords(head);
  }
  const fromPrimary = primary?.title || primary?.detail;
  if (fromPrimary) return truncateWords(fromPrimary);

  const rec = conversionProb?.recommended_action;
  if (rec) return truncateWords(rec);

  const days = overview?.lead_aging?.days_since_last_contact;
  if (verdict.kind === 'healthy' && norm(lead?.status) === 'not_interested') {
    return 'Lead marked not interested — closure looks consistent';
  }
  if (typeof days === 'number' && days >= 15) {
    return truncateWords(`No touch in ${days} days — lead may be going cold`);
  }
  const tc = stats?.total_call ?? 0;
  if (!tc && (stats?.engagement_score ?? 0) < 10) {
    return 'No engagement yet — start with a first call';
  }
  if (verdict.kind === 'review') {
    return 'Mixed signals — quick review recommended';
  }
  return truncateWords(conversionProb?.reasons?.[0] || 'Keep the next follow-up on track');
}

export function buildWhyActionBullets({
  conversationInsights,
  negativeSignals,
  overview,
  lead,
  stats,
}) {
  const out = [];
  const primary = conversationInsights?.primary_next_action;
  if (primary?.detail) out.push(`→ ${truncateWords(primary.detail, 18)}`);
  if (primary?.suggested_action) out.push(`→ ${truncateWords(primary.suggested_action, 18)}`);

  const neg = (negativeSignals || []).slice(0, 2);
  neg.forEach((raw) => {
    const s = String(raw).replace(/^[🟢🔴✅⛔−+]\s*/u, '').trim();
    if (s) out.push(`→ ${truncateWords(s, 16)}`);
  });

  const st = norm(lead?.status);
  const stg = norm(lead?.stage);
  if (st === 'not_interested' && getOverviewRecoveryScore(overview) >= 35) {
    out.push('→ Recovery data suggests revisit may be worthwhile');
  }
  if (stg === 'junk' && (stats?.engagement_score ?? 0) > 15) {
    out.push('→ Junk stage but some engagement logged — verify classification');
  }

  return [...new Set(out)].slice(0, 3);
}

export function buildIssuesList({
  verdict,
  overview,
  lead,
  conversationInsights,
  stats,
}) {
  if (verdict.kind === 'healthy') return null;

  const issues = [];
  const alerts = conversationInsights?.alerts || [];
  alerts.slice(0, 2).forEach((a) => {
    if (a?.title) issues.push(a.title);
  });

  const st = norm(lead?.status);
  const stg = norm(lead?.stage);
  if (st === 'not_interested' && !overview?.upcoming_task) {
    issues.push('No follow-up task scheduled after not-interested');
  }
  if (stg === 'junk' && (stats?.engagement_score ?? 0) >= 25) {
    issues.push('Possible misclassification vs recent engagement');
  }
  if (overview?.lead_aging?.is_dormant) {
    issues.push('Dormant: 15+ days without contact on an active lead');
  }

  const uniq = [...new Set(issues)];
  return uniq.length ? uniq.slice(0, 3) : null;
}

export function buildSignalBullets({ negativeSignals, stats, overview }) {
  const out = [];
  const days = overview?.lead_aging?.days_since_last_contact;
  if (typeof days === 'number' && days >= 30) {
    out.push(`No response signal: ${days} days since last contact`);
  }
  const missed = stats?.missed_calls ?? stats?.missed_call_count;
  if (missed > 2) out.push(`Multiple missed calls (${missed}) logged`);
  (negativeSignals || []).slice(0, 2).forEach((raw) => {
    const s = String(raw).replace(/^[🟢🔴✅⛔−+]\s*/u, '').trim();
    if (s) out.push(truncateWords(s, 16));
  });
  const eng = stats?.engagement_score ?? 0;
  if (eng < 25 && out.length < 3) out.push(`Low engagement score (${eng}/100)`);
  return out.slice(0, 3);
}

export function buildRiskTimeline({ overview, stats }) {
  const la = overview?.lead_aging;
  if (!la) return null;
  const out = [];
  if (la.last_contact) out.push(`Last meaningful touch: ${la.last_contact}`);
  const d = la.days_since_last_contact;
  if (typeof d === 'number') out.push(`${d} days since last activity`);
  if (la.is_dormant) out.push('Flagged dormant — lead going cold without action');
  return out.length ? out.slice(0, 3) : null;
}

export function shouldShowAgingStrip({ overview, stats }) {
  const d = overview?.lead_aging?.days_since_last_contact ?? stats?.days_since_last_contact;
  if (typeof d === 'number' && d > 15) return true;
  if (overview?.lead_aging?.is_dormant) return true;
  if (overview?.urgency?.show) return true;
  return false;
}

export function agingStripTag({ overview }) {
  if (overview?.urgency?.severity === 'high') return 'Critical';
  if (overview?.lead_aging?.is_dormant) return 'At Risk';
  return 'At Risk';
}

/**
 * Primary actions (max 2) for overview sticky bar — opens Create Task or Properties tab.
 * Keys: lead_followup | site_visit | properties
 */
export function pickPrimaryActions({ suggestedProperties = 0 }) {
  const followUp = { key: 'lead_followup', label: 'Schedule follow-up' };
  const siteVisit = { key: 'site_visit', label: 'Schedule site visit' };
  const properties = { key: 'properties', label: 'View properties' };
  const hasSuggested = Number(suggestedProperties) > 0;
  return [followUp, hasSuggested ? properties : siteVisit];
}

export function buildImpactLine(verdict, recoveryScore) {
  if (verdict.kind === 'recoverable' && recoveryScore >= 40) {
    return '→ Acting now can improve conversion odds vs leaving the lead idle';
  }
  if (verdict.kind === 'review') {
    return '→ A short review resolves conflicting pipeline signals';
  }
  return '→ Staying on cadence keeps probability and aging healthy';
}

export function buildLeadOverviewCopilotModel(input) {
  const {
    leadDetails,
    overview,
    stats,
    conversionProb,
    conversationInsights,
    positiveSignals,
    negativeSignals,
  } = input;

  const verdict = deriveVerdictKind({
    lead: leadDetails,
    overview,
    stats,
    conversionProb,
  });

  const recoveryScore = getOverviewRecoveryScore(overview);

  const statusMap = {
    healthy: { emoji: '🟢', label: 'No Action Needed', tone: 'green' },
    recoverable: { emoji: '🔴', label: 'Recoverable', tone: 'red' },
    review: { emoji: '🟡', label: 'Needs Review', tone: 'amber' },
  };
  const st = statusMap[verdict.kind];

  const aiMessage = buildAiHeadline({
    conversationInsights,
    conversionProb,
    overview,
    stats,
    verdict,
    lead: leadDetails,
  });

  const whyBullets = buildWhyActionBullets({
    conversationInsights,
    negativeSignals,
    overview,
    lead: leadDetails,
    stats,
  });

  const issues = buildIssuesList({
    verdict,
    overview,
    lead: leadDetails,
    conversationInsights,
    stats,
  });

  const showAging = shouldShowAgingStrip({ overview, stats });
  const daysSilent = overview?.lead_aging?.days_since_last_contact ?? stats?.days_since_last_contact;
  const agingMessage =
    overview?.urgency?.message ||
    (typeof daysSilent === 'number'
      ? `⚠️ ${daysSilent} days inactive — lead may be going cold`
      : null);

  const actions = pickPrimaryActions({
    suggestedProperties: overview?.suggested_properties ?? 0,
  });

  return {
    status: st,
    aiMessage,
    /** Raw API payload for sticky “Next action” rich layout (title + detail + suggested_action). */
    primaryNextAction: conversationInsights?.primary_next_action || null,
    confidence: verdict.confidence,
    actions,
    whyExpand: {
      bullets: whyBullets.length ? whyBullets : ['→ Use conversation insights and pipeline rules to choose the next touch'],
      impact: buildImpactLine(verdict, recoveryScore),
    },
    snapshot: {
      tempEmoji:
        (overview?.lead_temperature?.label || stats?.temperature_label || 'Cold') === 'Hot'
          ? '🔥'
          : (overview?.lead_temperature?.label || stats?.temperature_label || 'Cold') === 'Warm'
            ? '🌡️'
            : '❄️',
      tempLabel: overview?.lead_temperature?.label || stats?.temperature_label || 'Cold',
      engagement: stats?.engagement_score ?? 0,
      lastContact: overview?.lead_aging?.last_contact || stats?.last_contact_date || '—',
      stage: leadDetails?.stage || leadDetails?.pipeline_stage || '—',
    },
    issues,
    aging: showAging && agingMessage
      ? { message: agingMessage, tag: agingStripTag({ overview }) }
      : null,
    insights: {
      whyBullets:
        whyBullets.length > 0
          ? whyBullets
          : ['→ Review last touch, aging, and open tasks to pick the best next move'],
      impact: buildImpactLine(verdict, recoveryScore),
      signals: buildSignalBullets({ negativeSignals, stats, overview }),
      riskTimeline: buildRiskTimeline({ overview, stats }),
      positiveSignals: positiveSignals || [],
      negativeSignals: negativeSignals || [],
      recovery: getOverviewRecoveryPayload(overview),
      patternInsight: overview?.pattern_insight || null,
    },
    flags: {
      hideAggressiveActions: verdict.kind === 'healthy',
    },
  };
}
